/*
 * File: References.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Persists references api data for components
      This is also the static reference data we need for select fields in the app.  Rather than making multiple API
      calls when loading a form, you can get the select values from this object.  This also helps with API
      interaction, because some endpoints return numbers for the selected value, which cannot be displayed.
      Rather than making a request for a string value of a selected field, you can get the string value from
      from this object.  Also, these static values may be replaced with a reference endpoint that returns
      all the reference info
 */

import Api from "./Api";
import DataForFormSelect from "utils/DataForFormSelect";
import { flow, types } from "mobx-state-tree";

const OfficeModel = types.model("OfficeModel", {
  officeId: types.number,
  officeName: types.string
});

const FeeScheduleModel = types.model("FeeScheduleModel", {
  feeScheduleId: types.number,
  feeScheduleName: types.string
});

const LanguageModel = types.model("LanguageModel", {
  languageId: types.number,
  language: types.string
});

const MaritalStatusModel = types.model("MaritalStatusModel", {
  description: types.string,
  id: types.number
});

const EmploymentStatusModel = types.model("EmploymentStatusModel", {
  description: types.string,
  id: types.number
});

const PatientRelationshipModel = types.model("PatientRelationshipModel", {
  patientRelationshipId: types.number,
  patientRelationship: types.string
});

const Store = types
  .model("ReferencesStore", {
    offices: types.maybeNull(types.array(OfficeModel)),
    feeSchedules: types.maybeNull(types.array(FeeScheduleModel)),
    languages: types.maybeNull(types.array(LanguageModel)),
    maritalStatuses: types.maybeNull(types.array(MaritalStatusModel)),
    employmentStatuses: types.maybeNull(types.array(EmploymentStatusModel)),
    patientRelationship: types.maybeNull(types.array(PatientRelationshipModel))
  })
  .actions(self => ({
    getOffices: flow(function*() {
      if (!self.offices) self.offices = yield Api.fetchOffices();
      return self.offices;
    }),

    officeById: officeId => {
      if (self.offices && officeId) {
        let office = self.offices.find(o => o.officeId === officeId);
        return office.officeName || "";
      }
      return "";
    },

    getFeeSchedules: flow(function*() {
      if (!self.feeSchedules) self.feeSchedules = yield Api.fetchFeeSchedules();
      return self.feeSchedules;
    }),

    getLanguages: flow(function*() {
      if (!self.languages) self.languages = yield Api.fetchLanguages();
      return self.languages;
    }),

    getMaritalStatuses: flow(function*() {
      if (!self.maritalStatuses)
        self.maritalStatuses = yield Api.fetchMaritalStatuses();
      return self.maritalStatuses;
    }),

    getEmploymentStatuses: flow(function*() {
      if (!self.employmentStatuses)
        self.employmentStatuses = yield Api.fetchEmploymentStatuses();
      return self.employmentStatuses;
    }),

    getPatientRelationships: flow(function*() {
      if (!self.patientRelationships)
        self.patientRelationships = yield Api.fetchPatientRelationships();
      return self.patientRelationships;
    }),

    /**
     * Maps data for select inputs in form that will return an {id: '',name:''} object
     *
     * @param {String} reference
     * @return {Array}
     */
    forSelect: flow(function*(reference) {
      switch (reference) {
        case "offices": {
          let offices = yield self.getOffices();
          return DataForFormSelect(offices, "officeId", "officeName");
        }

        case "phoneTypes": {
          let phoneTypes = self.getPhoneTypes();
          return DataForFormSelect(phoneTypes, "id", "name");
        }

        case "feeSchedules": {
          let feeSchedules = yield self.getFeeSchedules();
          return DataForFormSelect(
            feeSchedules,
            "feeScheduleId",
            "feeScheduleName"
          );
        }

        case "languages": {
          let languages = yield self.getLanguages();
          return DataForFormSelect(languages, "languageId", "language");
        }

        case "sexTypes": {
          let sexTypes = self.getSexTypes();
          return DataForFormSelect(sexTypes, "id", "name");
        }
        case "maritalStatusTypes": {
          let maritalStatusTypes = yield self.getMaritalStatuses();
          return DataForFormSelect(maritalStatusTypes, "id", "description");
        }

        case "employmentStatusTypes": {
          let employmentStatusTypes = yield self.getEmploymentStatuses();
          return DataForFormSelect(employmentStatusTypes, "id", "description");
        }

        case "relationshipTypes": {
          let relationshipTypes = yield self.getPatientRelationships();
          return DataForFormSelect(
            relationshipTypes,
            "patientRelationshipId",
            "patientRelationship"
          );
        }

        default: {
          console.log("References 'forSelect' parameter is invalid", reference);
          break;
        }
      }
    })
  }))
  .views(() => ({
    getPhoneTypes() {
      return phoneTypes;
    },

    getSexTypes() {
      return sexTypes;
    }
  }));

export const phoneTypes = [
  { id: "0", name: "Home Phone" },
  { id: "1", name: "Work Phone" },
  { id: "2", name: "Mobile Phone" },
  { id: "3", name: "Other Phone" }
];

export const sexTypes = [
  { id: "0", name: "Male" },
  { id: "1", name: "Female" },
  { id: "2", name: "Unknown" }
];

export default Store;
